// File: frontend/src/config/origins.js
//
// THE TWO PESAFII APPLICATIONS, AND WHICH ONE THIS PAGE IS.
//
//   https://pesafii.net             the business (tenant) application, the public
//                                   Ambassador programme page (/ambassador) and
//                                   referred-business acquisition (/r/:code)
//   https://ambassador.pesafii.net  the signed-in PesaFii Ambassador application
//
// One bundle serves both hosts. App.js mounts a different application tree by
// host, and every cross-application link is built here, never from
// window.location.origin. That rule matters most for referral links: they must
// ALWAYS be https://pesafii.net/r/:code, or an Ambassador copying their link from
// the Ambassador host would hand out ambassador.pesafii.net/r/… instead.
//
// Preview deployments and local development cannot be the subdomain, so a
// non-production host may opt into the Ambassador tree with ?app=ambassador
// (remembered for the tab) or REACT_APP_APP=ambassador. Production hosts ignore
// both: pesafii.net is never switched into the Ambassador tree by a URL.

const clean = (v) => String(v || "").trim().replace(/\/+$/, "");

export const MAIN_ORIGIN = clean(process.env.REACT_APP_MAIN_ORIGIN) || "https://pesafii.net";
export const AMBASSADOR_ORIGIN = clean(process.env.REACT_APP_AMBASSADOR_ORIGIN) || "https://ambassador.pesafii.net";

const hostOf = (origin) => { try { return new URL(origin).host; } catch (_) { return ""; } };
const AMBASSADOR_HOST = hostOf(AMBASSADOR_ORIGIN);
const PRODUCTION_HOSTS = new Set([hostOf(MAIN_ORIGIN), "www.pesafii.net", AMBASSADOR_HOST]);
const OVERRIDE_KEY = "pesafii.app";

/** Which application tree this page should mount: "ambassador" or "main". */
export function currentApp(loc = typeof window !== "undefined" ? window.location : null) {
  if (!loc) return "main";
  if (loc.host === AMBASSADOR_HOST) return "ambassador";
  if (PRODUCTION_HOSTS.has(loc.host)) return "main";
  // Non-production only: an explicit, visible opt-in for previews and dev.
  try {
    const q = new URLSearchParams(loc.search || "").get("app");
    if (q === "ambassador" || q === "main") sessionStorage.setItem(OVERRIDE_KEY, q);
    const stored = sessionStorage.getItem(OVERRIDE_KEY);
    if (stored === "ambassador" || stored === "main") return stored;
  } catch (_) { /* storage unavailable: fall through */ }
  return process.env.REACT_APP_APP === "ambassador" ? "ambassador" : "main";
}

export const isAmbassadorApp = () => currentApp() === "ambassador";

/** An absolute URL on the business application, e.g. mainUrl("/dashboard"). */
export function mainUrl(path = "/") {
  return `${MAIN_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

/** An absolute URL on the Ambassador application, e.g. ambassadorUrl("/clients"). */
export function ambassadorUrl(path = "/") {
  return `${AMBASSADOR_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`;
}

/** The canonical referral link. Always the business site, whatever host we are on. */
export function referralUrl(code) {
  return `${MAIN_ORIGIN}/r/${encodeURIComponent(String(code || ""))}`;
}

/**
 * Is `path` a plain same-application relative path? The client-side mirror of
 * the server's return-path rule, used before a path is ever sent or followed:
 * no scheme, no protocol-relative //, no backslash, no whitespace or control
 * characters, and it must resolve to the same origin it started on.
 */
export function isSafeRelativePath(path) {
  if (typeof path !== "string" || path.length === 0 || path.length > 512) return false;
  if (!path.startsWith("/") || path.startsWith("//")) return false;
  // Control characters are refused on purpose (they hide inside paths).
  // eslint-disable-next-line no-control-regex
  if (/[\\\s\x00-\x1f\x7f]/.test(path)) return false;
  let decoded = path;
  for (let i = 0; i < 3; i += 1) {
    try { decoded = decodeURIComponent(decoded); } catch (_) { return false; }
    if (decoded.startsWith("//") || /\\/.test(decoded) || /^\/[a-z][a-z0-9+.-]*:/i.test(decoded)) return false;
  }
  try { return new URL(path, "https://x.invalid").origin === "https://x.invalid"; } catch (_) { return false; }
}
